/**
 * Recover from stale lazy chunks after a deploy.
 *
 * A tab loaded before a deploy still references the previous build's hashed
 * chunks. Once they are gone, the first lazy route it opens fails to import.
 * The fix is one page reload, which fetches the new index.html — but only one:
 * if the fresh page fails the same way, the deploy itself is broken and a
 * second reload would loop.
 *
 * Two entry points share one guard: the `vite:preloadError` listener (Vite
 * wraps every dynamic `import()` and fires it on failure, whatever the
 * browser's wording) and the app error boundary's fallback, which sniffs the
 * message for anything that reaches it unwrapped.
 */

const RELOAD_AT_KEY = 'chunk-reload-at';

/** A second failure within this window after a reload is not a stale chunk — stop reloading. */
const RELOAD_WINDOW_MS = 30_000;

let reloadPending = false;

export const isChunkLoadError = (error: unknown): boolean => {
  const message = (error as Error)?.message ?? '';
  return message.includes('Failed to fetch dynamically imported module') || // Chrome
    message.includes('error loading dynamically imported module') || // Firefox
    message.includes('Importing a module script failed') || // Safari, chunk 404
    message.includes('is not a valid JavaScript MIME type') || // Safari, chunk served as HTML
    message.includes('Loading chunk') ||
    message.includes('Loading CSS chunk');
};

/**
 * Reload the page unless this tab already reloaded for a stale chunk within
 * the last {@link RELOAD_WINDOW_MS}. Returns true when a reload is under way
 * (started now or by an earlier call on this page), so the caller can render
 * nothing instead of an error screen.
 */
export function reloadForStaleChunk(): boolean {
  if (reloadPending) return true;
  try {
    const last = Number(sessionStorage.getItem(RELOAD_AT_KEY));
    if (last && Date.now() - last < RELOAD_WINDOW_MS) return false;
    sessionStorage.setItem(RELOAD_AT_KEY, String(Date.now()));
  } catch {
    // No sessionStorage means no loop guard — never reload blind.
    return false;
  }
  reloadPending = true;
  window.location.reload();
  return true;
}

/** Reload once when Vite fails to load a dynamic import. Call before the app renders. */
export function installStaleChunkReload(): void {
  window.addEventListener('vite:preloadError', () => {
    // The error still propagates to the error boundary, whose fallback sees
    // the pending reload and renders nothing.
    reloadForStaleChunk();
  });
}

/** Test-only: reset module state between cases. */
export function __resetChunkReloadForTests(): void {
  reloadPending = false;
}
